/**
 * C4 — oversharing source data (Sites.Read.All + InformationProtectionPolicy.Read).
 *
 * Enumerates SharePoint/OneDrive sites and inspects each site's sharing
 * permissions for broadly-scoped links ("Anyone"/anonymous and organisation-wide),
 * which are the primary Copilot oversharing risk. All reads are delegated and
 * routed through the graph/ layer (§3.4). The site list is bounded by the admin's
 * chosen scan scope so the progressive scan (§6) stays responsive.
 */
import { graphGetPages, graphGet, graphBatch, type BatchStep, type BatchResult } from '@/graph/client';
import { CAPABILITY_SCOPES } from '@/auth/scopes';

export interface SiteRef {
  id: string;
  displayName: string;
  webUrl: string;
}

/** The kind of directory object holding a site-owner role. */
export type SiteOwnerType = 'user' | 'group' | 'siteGroup' | 'application' | 'unknown';

/** A single identity that holds an owner (full-control) role on a site. */
export interface SiteOwner {
  /** Directory object id, when the identity exposes one. */
  id?: string;
  displayName: string;
  type: SiteOwnerType;
  email?: string;
}

/** Richer site metadata used by the site-picker filters. */
export interface SiteInfo extends SiteRef {
  createdDateTime?: string;
  /** Site lastModifiedDateTime — used as a last-activity proxy. */
  lastActivity?: string;
  /**
   * Comma-joined owner display names — kept for the picker's free-text owner
   * filter and any single-string consumer. `owners` is the structured form.
   */
  owner?: string;
  /** Every identity holding an owner role on the site (C14). */
  owners?: SiteOwner[];
  /** Storage used by the default document library, in bytes. */
  sizeBytes?: number;
}

export interface SiteSharingSignal {
  anonymousLinks: number;
  orgWideLinks: number;
}

const SITE_SCOPES = CAPABILITY_SCOPES.C4.scopes;
/** C14 default: the site usage report (Reports.Read.All) — lists every site. */
const REPORT_SCOPES = CAPABILITY_SCOPES.C14.scopes;
/** C15 opt-in: the broad, write-capable scope needed to read a site's SharePoint owners. */
const SITE_OWNER_SCOPES = CAPABILITY_SCOPES.C15.scopes;

/** Split an array into fixed-size chunks (Graph $batch caps at 20 steps). */
function chunk<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/** Stable identity key for de-duplicating owners across sources. */
function ownerKey(o: SiteOwner): string {
  return `${o.type}:${(o.id ?? o.displayName).toLowerCase()}`;
}

/** De-duplicate a list of owners, keeping first-seen order. */
function dedupeOwners(owners: readonly SiteOwner[]): SiteOwner[] {
  const seen = new Map<string, SiteOwner>();
  for (const o of owners) if (!seen.has(ownerKey(o))) seen.set(ownerKey(o), o);
  return [...seen.values()];
}

/** The site's primary owner, mapped from a usage-report row, as a SiteOwner. */
function reportOwner(displayName?: string, principalName?: string): SiteOwner[] {
  const name = displayName ?? principalName;
  if (!name) return [];
  return [{ displayName: name, type: 'user', email: principalName }];
}

/** One row of the SharePoint site usage detail report (JSON shape). */
export interface SiteUsageEntry {
  siteId: string;
  siteUrl: string;
  ownerDisplayName?: string;
  ownerPrincipalName?: string;
  rootWebTemplate?: string;
}

/**
 * Enumerate EVERY SharePoint site in the tenant via the site usage detail report
 * (delegated Reports.Read.All), read as inline JSON (`?$format=application/json`) so
 * all traffic stays on graph.microsoft.com. Unlike group enumeration (group sites
 * only) and `/sites?search=*` (capped at 500) this covers non-group sites too, and
 * each row carries the site URL and primary owner.
 *
 * NOTE: if the tenant conceals report data (the default since 2021), `siteUrl` and
 * the owner fields come back anonymised until a Global Admin disables "Conceal user,
 * group, and site names in all reports". Deleted sites are filtered out.
 */
export async function listAllSitesUsage(
  period: 'D7' | 'D30' | 'D90' | 'D180' = 'D30',
): Promise<SiteUsageEntry[]> {
  const out: SiteUsageEntry[] = [];
  for await (const page of graphGetPages<{
    siteId?: string;
    siteUrl?: string;
    ownerDisplayName?: string;
    ownerPrincipalName?: string;
    isDeleted?: boolean | string;
    rootWebTemplate?: string;
  }>(`/reports/getSharePointSiteUsageDetail(period='${period}')`, {
    scopes: REPORT_SCOPES,
    query: { $format: 'application/json' },
  })) {
    for (const r of page) {
      if (!r.siteId) continue;
      if (r.isDeleted === true || String(r.isDeleted).toLowerCase() === 'true') continue;
      out.push({
        siteId: r.siteId,
        siteUrl: r.siteUrl ?? '',
        ownerDisplayName: r.ownerDisplayName ?? undefined,
        ownerPrincipalName: r.ownerPrincipalName ?? undefined,
        rootWebTemplate: r.rootWebTemplate ?? undefined,
      });
    }
  }
  return out;
}

/** Build the report-primary-owner list for a usage entry (its "Owner"). */
export function usageEntryOwners(e: SiteUsageEntry): SiteOwner[] {
  return reportOwner(e.ownerDisplayName, e.ownerPrincipalName);
}

/* ---- SharePoint site owners (Sites.FullControl.All) --------------------------
 * The site's own "Owners" — distinct from the Microsoft 365 group owners above.
 * Read from the site's permissions, which for a delegated token require
 * Sites.FullControl.All (there is no read-only scope for it). As a Global/SharePoint
 * admin this works tenant-wide. Note: Graph surfaces the owner-role identities on
 * the site — the SharePoint "Owners" group and any directly-granted owners — not
 * always each individual member of that group (that needs SharePoint's own REST). */

/** Graph identitySet on a site `permission` (v1.0 + legacy shapes). */
interface IdentitySet {
  user?: { id?: string; displayName?: string; email?: string } | null;
  group?: { id?: string; displayName?: string; email?: string } | null;
  siteGroup?: { id?: string; displayName?: string; loginName?: string } | null;
  siteUser?: { id?: string; displayName?: string; email?: string; loginName?: string } | null;
  application?: { id?: string; displayName?: string } | null;
}

interface SitePermission {
  roles?: string[] | null;
  grantedToV2?: IdentitySet | null;
  grantedToIdentitiesV2?: IdentitySet[] | null;
  grantedTo?: IdentitySet | null;
  grantedToIdentities?: IdentitySet[] | null;
}

/** A permission is an ownership grant if any role is owner / full control. */
function isOwnerRole(roles?: string[] | null): boolean {
  return (roles ?? []).some((r) => {
    const x = r.toLowerCase().replace(/\s+/g, '');
    return x === 'owner' || x === 'fullcontrol';
  });
}

/** Map a Graph identitySet to a SiteOwner, preferring the most specific identity. */
function identityToOwner(ids?: IdentitySet | null): SiteOwner | null {
  if (!ids) return null;
  if (ids.user) return { id: ids.user.id, displayName: ids.user.displayName ?? ids.user.email ?? 'Unknown user', type: 'user', email: ids.user.email ?? undefined };
  if (ids.siteUser) return { id: ids.siteUser.id, displayName: ids.siteUser.displayName ?? ids.siteUser.email ?? ids.siteUser.loginName ?? 'Unknown user', type: 'user', email: ids.siteUser.email ?? undefined };
  if (ids.group) return { id: ids.group.id, displayName: ids.group.displayName ?? ids.group.email ?? 'Unknown group', type: 'group', email: ids.group.email ?? undefined };
  if (ids.siteGroup) return { id: ids.siteGroup.id, displayName: ids.siteGroup.displayName ?? ids.siteGroup.loginName ?? 'Unknown site group', type: 'siteGroup' };
  if (ids.application) return { id: ids.application.id, displayName: ids.application.displayName ?? 'Application', type: 'application' };
  return null;
}

/**
 * The SharePoint owners of a site — identities holding an owner / full-control role
 * in the site's permissions (Sites.FullControl.All). Best-effort per site.
 */
export async function getSitePermissionOwners(siteId: string): Promise<SiteOwner[]> {
  const res = await graphGet<{ value?: SitePermission[] }>(
    `/sites/${encodeURIComponent(siteId)}/permissions`,
    { scopes: SITE_OWNER_SCOPES },
  );
  const out: SiteOwner[] = [];
  for (const p of res.value ?? []) {
    if (!isOwnerRole(p.roles)) continue;
    const identities: (IdentitySet | null | undefined)[] = [
      p.grantedToV2,
      p.grantedTo,
      ...(p.grantedToIdentitiesV2 ?? []),
      ...(p.grantedToIdentities ?? []),
    ];
    for (const ids of identities) {
      const o = identityToOwner(ids);
      if (o) out.push(o);
    }
  }
  return dedupeOwners(out);
}

/**
 * Search sites by keyword (v1.0 `/sites?search=`), for the "pick which sites to
 * scan" UI. Requests ONLY `Sites.Read.All` so it works under any capability that
 * already holds that scope (C4/C12/C13) and follows `@odata.nextLink` to completion;
 * pass `max` only to cap the result set. NOTE: SharePoint search caps results (~500)
 * and `getAllSites`/`GET /sites` are application-only, so this is NOT a complete
 * tenant enumeration — the C14 site-owners report enumerates the whole tenant via
 * `listAllSitesUsage` instead. Returns created + last-activity dates from the site
 * object; owner/size are added by `enrichSites`.
 */
export async function searchSites(query: string, max?: number): Promise<SiteInfo[]> {
  const out: SiteInfo[] = [];
  const q = query.trim() || '*';
  const cap = typeof max === 'number' && max > 0 ? max : Infinity;
  for await (const page of graphGetPages<{
    id?: string;
    displayName?: string;
    name?: string;
    webUrl?: string;
    createdDateTime?: string;
    lastModifiedDateTime?: string;
  }>('/sites', {
    scopes: ['Sites.Read.All'],
    version: 'v1.0',
    query: { $select: 'id,displayName,name,webUrl,createdDateTime,lastModifiedDateTime', search: q },
  })) {
    for (const s of page) {
      if (!s.id) continue;
      out.push({
        id: s.id,
        displayName: s.displayName ?? s.name ?? s.webUrl ?? s.id,
        webUrl: s.webUrl ?? '',
        createdDateTime: s.createdDateTime,
        lastActivity: s.lastModifiedDateTime,
      });
      if (out.length >= cap) return out;
    }
  }
  return out;
}

interface DriveMeta {
  owner?: { user?: { displayName?: string }; group?: { displayName?: string } };
  quota?: { used?: number };
}

/**
 * Best-effort enrichment for the picker: batch-read each site's default document
 * library for its owner (a quick display value) and storage used, so the picker
 * can filter by owner and size. Requests are chunked to Graph's 20-step $batch
 * limit, so selecting a large result set no longer overflows a single batch.
 * Sites whose drive can't be read keep their basic info. (The C14 site-owners
 * report is built separately from Microsoft 365 group data, not from this.)
 */
export async function enrichSites(sites: SiteInfo[]): Promise<SiteInfo[]> {
  if (sites.length === 0) return sites;

  const steps: BatchStep[] = sites.map((s, i) => ({
    id: String(i),
    method: 'GET',
    url: `/sites/${encodeURIComponent(s.id)}/drive?$select=owner,quota`,
  }));

  const byId = new Map<string, BatchResult>();
  for (const group of chunk(steps, 20)) {
    const responses = await graphBatch(group, { scopes: ['Sites.Read.All'] }).catch(() => [] as BatchResult[]);
    for (const r of responses) byId.set(r.id, r);
  }

  return sites.map((s, i) => {
    const r = byId.get(String(i));
    if (!r || r.status < 200 || r.status >= 300) return s;
    const body = r.body as DriveMeta | undefined;
    const owner = body?.owner?.user?.displayName ?? body?.owner?.group?.displayName;
    const used = body?.quota?.used;
    return {
      ...s,
      owner: owner ?? s.owner,
      sizeBytes: typeof used === 'number' ? used : s.sizeBytes,
    };
  });
}

interface PermissionLike {
  link?: { scope?: string | null; type?: string | null } | null;
}

/** Inspect one site's permissions for broadly-scoped sharing links. */
export async function getSiteSharingSignal(siteId: string): Promise<SiteSharingSignal> {
  const res = await graphGet<{ value?: PermissionLike[] }>(`/sites/${encodeURIComponent(siteId)}/permissions`, {
    scopes: SITE_SCOPES,
  });
  let anonymousLinks = 0;
  let orgWideLinks = 0;
  for (const p of res.value ?? []) {
    const scope = (p.link?.scope ?? '').toLowerCase();
    if (scope === 'anonymous') anonymousLinks += 1;
    else if (scope === 'organization') orgWideLinks += 1;
  }
  return { anonymousLinks, orgWideLinks };
}

/**
 * Count published sensitivity labels (InformationProtectionPolicy.Read). Having
 * labels published is a positive readiness signal; none published means content
 * can't be classified — an oversharing risk. Best-effort; null if unreadable.
 */
export async function getPublishedLabelCount(): Promise<number | null> {
  try {
    // Share C4's full scope-set so the label read reuses the same delegated token
    // as the site scan — one consent, one popup (§4).
    const res = await graphGet<{ value?: unknown[] }>('/informationProtection/policy/labels', {
      scopes: SITE_SCOPES,
      version: 'v1.0',
      bestEffort: true,
    });
    return (res.value ?? []).length;
  } catch {
    return null;
  }
}
